import {
  ALLOCATION_UNITS,
  AllocationUnit,
  ConversionSource,
  convertAllocationQuantity,
  normalizeQuantityUnit,
} from './dimensionConversion';
import { formatUnit, normalizeUnitCode } from './units';
import { formatLocalizedQuantity } from './numberFormatting';
import { roundDecimalHalfUp } from './money';

export const COMMERCIAL_UNITS: readonly AllocationUnit[] = ALLOCATION_UNITS;
/** Active units offered for every new order, PZ, WZ and transport line. */
export const ACTIVE_QUANTITY_UNITS = ALLOCATION_UNITS;
/** Read-only compatibility for rows created before the dimensional-only scope. */
export const READABLE_QUANTITY_UNITS = ['pcs', 'm2', 'm3', 'kg', 'm', 'mb', 'l'] as const;
export type QuantityUnit = (typeof READABLE_QUANTITY_UNITS)[number];
// Every dimensional unit can be either side of the same conversion. There is
// no catalog/warehouse unit hidden behind this list.
export const COMMERCIAL_PRICING_CONVERSION_UNITS: readonly AllocationUnit[] = COMMERCIAL_UNITS;
export const MAX_CUSTOMER_PHYSICAL_QUANTITY = 1_000_000;
export const MAX_CUSTOMER_PRICING_QUANTITY = 99_999_999_999.999;
export const COMMERCIAL_CONVERSION_ERRORS = {
  INVALID_QUANTITY: 'INVALID_QUANTITY',
  INVALID_UNIT: 'INVALID_UNIT',
  UNSUPPORTED_PAIR: 'UNSUPPORTED_PAIR',
  MISSING_DIMENSIONS: 'MISSING_DIMENSIONS',
  INCONSISTENT_QUANTITIES: 'INCONSISTENT_QUANTITIES',
} as const;

export interface CommercialQuantityValue {
  physicalQuantity: number;
  physicalUnit: QuantityUnit;
  pricingQuantity: number;
  pricingUnit: QuantityUnit;
  /** Immutable dimensional snapshot carried by an already persisted line. */
  quantityPcs?: number | null;
  quantityM2?: number | null;
  quantityM3?: number | null;
  /** Optional presentation conversion, independent from the unit used for price. */
  conversionQuantity?: number;
  conversionUnit?: AllocationUnit | null;
}

export interface QuantitySet {
  pcs: number | null;
  m2: number | null;
  m3: number | null;
}

interface FormatQuantitySetOptions {
  absolute?: boolean;
  fallbackQuantity?: number | null;
  fallbackUnit?: QuantityUnit | null;
  emptyText?: string;
}

export function isAllocationUnit(value: unknown): value is AllocationUnit {
  return (COMMERCIAL_UNITS as readonly unknown[]).includes(value);
}

export function asQuantityUnit(value: unknown, fallback: QuantityUnit = 'pcs'): QuantityUnit {
  const normalized = normalizeUnitCode(String(value || fallback))
    ?? normalizeQuantityUnit(String(value || fallback));
  return (READABLE_QUANTITY_UNITS as readonly string[]).includes(normalized)
    ? normalized as QuantityUnit
    : fallback;
}

export function asCommercialUnit(value: unknown, fallback: AllocationUnit = 'pcs'): AllocationUnit {
  const normalized = normalizeQuantityUnit(String(value || fallback));
  return (COMMERCIAL_UNITS as readonly string[]).includes(normalized)
    ? normalized as AllocationUnit
    : fallback;
}

// Two quantities in different units agree when they differ by at most half a
// unit of the stored precision (0.0005, inclusive). A whole-piece rounding such
// as 809 pcs = 40.0455 m3 sits exactly on that edge, and a plain 3-decimal
// equality rounds it away from the 40.045 the operator sees. Anything wider
// (e.g. 40 m3 vs 808 pcs, or a small quantity) is a real mismatch.
const COMMERCIAL_CONVERSION_TOLERANCE = 0.0005;
function quantitiesAgree(exact: number, entered: number): boolean {
  return Math.abs(Number(exact) - Number(entered)) <= COMMERCIAL_CONVERSION_TOLERANCE + 1e-9;
}

// The one half-up rounding (money.ts roundDecimalHalfUp) at 3 decimals, as
// backend roundCommercialQuantity: 70 × 0.05175 = 3.6224999… still gives 3.623.
export function roundCommercialQuantity(value: number): number {
  return roundDecimalHalfUp(value, 3);
}

export function convertCommercialQuantity(
  quantity: number,
  sourceUnit: AllocationUnit,
  targetUnit: AllocationUnit,
  dimensions: ConversionSource,
): { value: number | null; exactValue: number | null; error: string | null } {
  const result = convertAllocationQuantity({
    ...dimensions,
    quantity,
    sourceUnit,
    targetUnit,
  });

  return {
    value: result.exactValue == null ? null : roundCommercialQuantity(result.exactValue),
    exactValue: result.exactValue,
    error: result.error,
  };
}

/**
 * Convert an entered amount through an immutable persisted quantity snapshot.
 * When no snapshot exists, dimensions provide the first conversion set. This
 * is the single frontend home for choosing between those two sources.
 */
export function convertCommercialQuantityUsingSnapshot(
  quantity: number,
  sourceUnit: AllocationUnit,
  targetUnit: AllocationUnit,
  dimensions: ConversionSource,
  storedSet: QuantitySet | null,
): { value: number | null; exactValue: number | null; error: string | null } {
  if (!storedSet) {
    return convertCommercialQuantity(quantity, sourceUnit, targetUnit, dimensions);
  }

  const sourceAmount = getQuantitySetValue(storedSet, sourceUnit);
  const targetAmount = getQuantitySetValue(storedSet, targetUnit);
  if (sourceAmount === null || targetAmount === null || sourceAmount === 0) {
    return {
      value: null,
      exactValue: null,
      error: COMMERCIAL_CONVERSION_ERRORS.MISSING_DIMENSIONS,
    };
  }

  const exactValue = Number(quantity) * targetAmount / sourceAmount;
  if (!Number.isFinite(exactValue)) {
    return {
      value: null,
      exactValue: null,
      error: COMMERCIAL_CONVERSION_ERRORS.INVALID_QUANTITY,
    };
  }
  return { value: roundCommercialQuantity(exactValue), exactValue, error: null };
}

/**
 * Whether the line's own dimensions can convert `unit` into every other unit
 * the stored set carries (the backend validates a changed pair against them).
 */
function dimensionsConvertSet(storedSet: QuantitySet, unit: AllocationUnit, dimensions: ConversionSource): boolean {
  const targets = COMMERCIAL_UNITS.filter((target) => target !== unit && getQuantitySetValue(storedSet, target) !== null);
  return targets.length > 0 && targets.every((target) => !convertCommercialQuantity(1, unit, target, dimensions).error);
}

/**
 * The snapshot for converting an amount entered on a SAVED line (frontend side
 * of the backend pair rule, `validateCommercialQuantityPair`):
 * - the amount is the saved one (the same goods re-expressed) → the stored set,
 *   so an accepted rounded pair (809 szt. = 40,046 m³) and its value stay;
 * - a changed amount → `null`: convert from the line's own dimensions exactly as
 *   the backend validates it (80,092 m³ = 1618,02 szt., not the stored ratio's
 *   1618 szt. that the backend rejects);
 * - a changed amount on a line whose dimensions cannot convert (legacy, „tylko
 *   w sztukach”) → the stored set scaled, as before.
 */
export function quantitySetForEnteredAmount(
  storedSet: QuantitySet | null,
  amount: number | null,
  unit: AllocationUnit,
  dimensions: ConversionSource,
): QuantitySet | null {
  if (!storedSet) return null;
  if (amount !== null && getQuantitySetValue(storedSet, unit) === roundCommercialQuantity(Number(amount))) return storedSet;
  return dimensionsConvertSet(storedSet, unit, dimensions) ? null : storedSet;
}

/**
 * The quantity set saved with a changed amount of a saved line: rebuilt from the
 * accepted pair and the line's own dimensions (the set the backend validates),
 * or the stored set scaled when the amount is unchanged or the dimensions cannot
 * convert. Never today's catalog: `dimensions` are the line's own.
 */
export function quantitySetForAppliedPair(
  storedSet: QuantitySet,
  value: CommercialQuantityValue,
  dimensions: ConversionSource,
): QuantitySet | null {
  if (!isAllocationUnit(value.physicalUnit)) return null;
  const snapshot = quantitySetForEnteredAmount(storedSet, value.physicalQuantity, value.physicalUnit, dimensions);
  if (snapshot) return scaleQuantitySetToAmount(snapshot, value.physicalQuantity, value.physicalUnit);
  const { quantityPcs: _pcs, quantityM2: _m2, quantityM3: _m3, ...pair } = value;
  return buildQuantitySetFromPair(pair, dimensions);
}

/**
 * The pair a quantity editor is about to apply, checked with the backend rule
 * before saving. Only a real mismatch blocks (`INCONSISTENT_QUANTITIES`):
 * a line without dimensions keeps its previous behaviour.
 */
export function getCommercialPairMismatch(
  value: Pick<CommercialQuantityValue, 'physicalQuantity' | 'physicalUnit' | 'pricingQuantity' | 'pricingUnit' | 'conversionQuantity' | 'conversionUnit'>,
  dimensions: ConversionSource,
): 'pricing' | 'conversion' | null {
  const check = (quantity: number, unit: unknown, rule: typeof validateCommercialQuantityPair) => rule(
    roundCommercialQuantity(value.physicalQuantity),
    value.physicalUnit,
    roundCommercialQuantity(quantity),
    unit,
    dimensions,
  ).error === COMMERCIAL_CONVERSION_ERRORS.INCONSISTENT_QUANTITIES;
  const inconsistent = (quantity: number, unit: unknown) => check(quantity, unit, validateCommercialQuantityPair);
  // The price pair is judged like the backend persists it: whole pieces next to
  // a document amount pass (decision 2026-10-30-faktura-zrodlem-prawdy).
  if (Number(value.pricingQuantity) > 0 && check(value.pricingQuantity, value.pricingUnit, validatePersistedQuantityPair)) return 'pricing';
  if (value.conversionUnit && Number(value.conversionQuantity) > 0
    && inconsistent(Number(value.conversionQuantity), value.conversionUnit)) return 'conversion';
  return null;
}

/**
 * Whole pieces only (owner 2026-10-06: „sztuki wybieramy całe”). An amount in m²/m³
 * of a line whose dimensions convert to pieces must be a whole number of szt.
 * Returns the exact (fractional) pieces when it is not, otherwise null.
 * - `explicitPieces`: the line's own szt. amount (price amount or conversion in
 *   szt.), judged as it is;
 * - otherwise the pieces from the line's dimensions: whole when a whole number of
 *   szt. forms a pair the backend accepts with the amount (40,046 m³ = 809 szt.
 *   of a 600 × 2500 × 33 mm board), fractional otherwise (80,092 m³ = 1618,02 szt.;
 *   „Zaokrąglij w dół/w górę” in „Przelicznik jednostek” gives 1618 or 1619 szt.).
 * Lines in szt., without dimensions or „tylko w sztukach” are never judged.
 */
export function getFractionalPieces(
  physicalQuantity: unknown,
  physicalUnit: unknown,
  dimensions: ConversionSource,
  explicitPieces?: unknown,
): number | null {
  if (dimensions.countInPiecesOnly === true) return null;
  if (physicalUnit !== 'm2' && physicalUnit !== 'm3') return null;
  const quantity = Number(physicalQuantity);
  if (!Number.isFinite(quantity) || quantity <= 0) return null;
  const explicit = Number(explicitPieces);
  if (explicitPieces != null && Number.isFinite(explicit) && explicit > 0) {
    return isWholePieceCount(explicit) ? null : explicit;
  }
  const pieces = convertCommercialQuantity(quantity, physicalUnit, 'pcs', dimensions).exactValue;
  if (pieces === null || !Number.isFinite(pieces) || isWholePieceCount(pieces)) return null;
  const whole = Math.round(pieces);
  return whole > 0 && validateCommercialQuantityPair(
    roundCommercialQuantity(quantity),
    physicalUnit,
    whole,
    'pcs',
    dimensions,
  ).valid ? null : pieces;
}

/**
 * Invoice print tolerance (owner 2026-10-22, docs/decisions/2026-10-22-pelne-sztuki-na-fakturze.md):
 * an invoice prints m² to 0,01, so a printed amount within 0,005 m² of whole pieces
 * is those pieces (717,19 m² = 250 szt. of a 2,86875-m² board). m³ is printed to
 * 0,001 and keeps the usual 0,0011. Backend mirror: commercialQuantity.js INVOICE_PRINT_TOLERANCE
 * (same literal; testCommercialQuantity.js and commercialQuantity.test.ts pin it).
 */
export const INVOICE_PRINT_TOLERANCE: Readonly<Record<AllocationUnit, number>> = Object.freeze({ pcs: 0.0011, m2: 0.005, m3: 0.0011 });

/** The tolerance the rounding panel uses: 1618,0000 szt. is whole, 1618,02 szt. is not. */
export function isWholePieceCount(pieces: number): boolean {
  return Math.abs(pieces - Math.round(pieces)) <= 0.0001;
}

function isPositiveCommercialQuantity(value: unknown): boolean {
  const quantity = Number(value);
  return Number.isFinite(quantity)
    && quantity >= 0.001
    && quantity <= MAX_CUSTOMER_PRICING_QUANTITY
    && Math.abs(quantity - roundCommercialQuantity(quantity)) < 1e-9;
}

/** Frontend mirror of backend validateCommercialQuantityPair. */
export function validateCommercialQuantityPair(
  physicalQuantity: number,
  physicalUnit: unknown,
  pricingQuantity: number,
  pricingUnit: unknown,
  dimensions: ConversionSource,
): { valid: boolean; error: string | null } {
  if (!isPositiveCommercialQuantity(physicalQuantity) || !isPositiveCommercialQuantity(pricingQuantity)) {
    return { valid: false, error: COMMERCIAL_CONVERSION_ERRORS.INVALID_QUANTITY };
  }
  if (!isAllocationUnit(physicalUnit) || !isAllocationUnit(pricingUnit)) {
    return { valid: false, error: COMMERCIAL_CONVERSION_ERRORS.INVALID_UNIT };
  }
  if (physicalUnit === pricingUnit) {
    return roundCommercialQuantity(physicalQuantity) === roundCommercialQuantity(pricingQuantity)
      ? { valid: true, error: null }
      : { valid: false, error: COMMERCIAL_CONVERSION_ERRORS.INCONSISTENT_QUANTITIES };
  }

  const forward = convertCommercialQuantity(
    physicalQuantity,
    physicalUnit,
    pricingUnit,
    dimensions,
  );
  if (forward.error || forward.exactValue === null) {
    return {
      valid: false,
      error: forward.error === 'MISSING_DIMENSIONS'
        ? COMMERCIAL_CONVERSION_ERRORS.MISSING_DIMENSIONS
        : COMMERCIAL_CONVERSION_ERRORS.UNSUPPORTED_PAIR,
    };
  }
  const reverse = convertAllocationQuantity({
    ...dimensions,
    quantity: pricingQuantity,
    sourceUnit: pricingUnit,
    targetUnit: physicalUnit,
  });
  if (reverse.error || reverse.exactValue === null) {
    return {
      valid: false,
      error: reverse.error === 'MISSING_DIMENSIONS'
        ? COMMERCIAL_CONVERSION_ERRORS.MISSING_DIMENSIONS
        : COMMERCIAL_CONVERSION_ERRORS.UNSUPPORTED_PAIR,
    };
  }

  const forwardMatches = quantitiesAgree(forward.exactValue, pricingQuantity);
  const reverseMatches = quantitiesAgree(reverse.exactValue, physicalQuantity);
  return forwardMatches || reverseMatches
    ? { valid: true, error: null }
    : { valid: false, error: COMMERCIAL_CONVERSION_ERRORS.INCONSISTENT_QUANTITIES };
}

/**
 * Frontend mirror of backend validatePersistedQuantityPair
 * (decision 2026-10-30-faktura-zrodlem-prawdy): a saved physical/price pair whose
 * physical side is whole pieces is accepted next to any m²/m³ document amount
 * (65 szt. / 23,53 m²); a fractional physical piece count stays
 * INCONSISTENT_QUANTITIES. Deriving quantities keeps validateCommercialQuantityPair.
 */
export function validatePersistedQuantityPair(
  physicalQuantity: number,
  physicalUnit: unknown,
  pricingQuantity: number,
  pricingUnit: unknown,
  dimensions: ConversionSource,
): { valid: boolean; error: string | null; matchesDimensions?: false } {
  const result = validateCommercialQuantityPair(physicalQuantity, physicalUnit, pricingQuantity, pricingUnit, dimensions);
  if (result.error !== COMMERCIAL_CONVERSION_ERRORS.INCONSISTENT_QUANTITIES || !isAllocationUnit(physicalUnit)) return result;
  // Only a printed area/volume differs from the pieces; pieces compare with pieces.
  if (pricingUnit !== 'm2' && pricingUnit !== 'm3') return result;
  const pieces = physicalUnit === 'pcs'
    ? Number(physicalQuantity)
    : convertCommercialQuantity(physicalQuantity, physicalUnit, 'pcs', dimensions).exactValue;
  const whole = Math.round(Number(pieces));
  const back = physicalUnit === 'pcs' ? whole : convertCommercialQuantity(whole, 'pcs', physicalUnit, dimensions).exactValue;
  const wholePieces = whole > 0 && back !== null && (physicalUnit === 'pcs'
    ? Math.abs(Number(physicalQuantity) - whole) <= 1e-9
    : quantitiesAgree(back, physicalQuantity));
  return wholePieces ? { valid: true, error: null, matchesDimensions: false } : result;
}

/**
 * Build all derivable views of one operator-entered amount. The source unit is
 * only the way the operator entered this transaction; no component becomes a
 * product-wide warehouse/base unit.
 */
export function createQuantitySet(
  quantity: number,
  unit: AllocationUnit,
  dimensions: ConversionSource,
): QuantitySet {
  const numericQuantity = Number(quantity);
  const result: QuantitySet = { pcs: null, m2: null, m3: null };
  if (!Number.isFinite(numericQuantity)) return result;

  const sign = numericQuantity < 0 ? -1 : 1;
  const absoluteQuantity = Math.abs(numericQuantity);
  if (absoluteQuantity === 0) return { pcs: 0, m2: 0, m3: 0 };

  COMMERCIAL_UNITS.forEach((targetUnit) => {
    if (targetUnit === unit) {
      result[targetUnit] = roundCommercialQuantity(numericQuantity);
      return;
    }
    const converted = convertCommercialQuantity(absoluteQuantity, unit, targetUnit, dimensions);
    if (!converted.error && converted.exactValue !== null) {
      result[targetUnit] = roundCommercialQuantity(converted.exactValue * sign);
    }
  });
  return result;
}

/**
 * Frontend mirror of backend buildQuantityEquivalentsFromPair. Prefer a
 * validated explicit conversion snapshot (including deliberate whole-piece
 * rounding), then the commercial price pair, and finally the entered side.
 */
export function buildQuantitySetFromPair(
  value: CommercialQuantityValue,
  dimensions: ConversionSource,
): QuantitySet | null {
  if (!isAllocationUnit(value.physicalUnit)) return null;

  // Once any component was persisted, the set is atomic: a NULL sibling means
  // "not derivable at the time", not "recalculate it from today's catalog".
  // A deliberate edit produces a fresh value without these fields and is then
  // validated/rebuilt from the newly accepted pair below.
  if (hasPersistedQuantitySet(value as unknown as Record<string, unknown>)) {
    return quantitySetFromFields(value as unknown as Record<string, unknown>);
  }
  // „Liczymy tylko w sztukach”: only the line's own quantity, no m² / m³ from
  // the dimensions (backend buildQuantityEquivalentsFromPair).
  if (dimensions?.countInPiecesOnly === true) {
    return createQuantitySet(value.physicalQuantity, value.physicalUnit, dimensions);
  }

  const candidates = [
    { quantity: value.conversionQuantity, unit: value.conversionUnit },
    { quantity: value.pricingQuantity, unit: value.pricingUnit },
  ];
  // Whole pieces are counted, never re-derived from an area/volume amount
  // (backend buildQuantityEquivalentsFromPair; 65 szt. stays 65, not 65,001).
  const wholePieces = value.physicalUnit === 'pcs' && Number.isInteger(Number(value.physicalQuantity));
  for (const candidate of wholePieces ? [] : candidates) {
    if (
      !isAllocationUnit(candidate.unit)
      || candidate.unit === value.physicalUnit
      || !isPositiveCommercialQuantity(candidate.quantity)
    ) {
      continue;
    }
    const validation = validateCommercialQuantityPair(
      value.physicalQuantity,
      value.physicalUnit,
      Number(candidate.quantity),
      candidate.unit,
      dimensions,
    );
    if (validation.valid) {
      return createQuantitySet(Number(candidate.quantity), candidate.unit, dimensions);
    }
  }

  return createQuantitySet(value.physicalQuantity, value.physicalUnit, dimensions);
}

export function quantitySetField(prefix: string, unit: AllocationUnit): string {
  const suffix = unit === 'pcs' ? 'Pcs' : unit.toUpperCase();
  return `${prefix}${suffix}`;
}

export function quantitySetFromFields(
  source: Record<string, unknown> | null | undefined,
  prefix = 'quantity',
): QuantitySet {
  return COMMERCIAL_UNITS.reduce<QuantitySet>((set, unit) => {
    const rawValue = source?.[quantitySetField(prefix, unit)];
    const value = rawValue === null || rawValue === undefined || rawValue === ''
      ? Number.NaN
      : Number(rawValue);
    set[unit] = Number.isFinite(value) ? roundCommercialQuantity(value) : null;
    return set;
  }, { pcs: null, m2: null, m3: null });
}

export function hasPersistedQuantitySet(
  source: Record<string, unknown> | null | undefined,
  prefix = 'quantity',
): boolean {
  return Object.values(quantitySetFromFields(source, prefix)).some((value) => value !== null);
}

export function quantitySetToFields(set: QuantitySet, prefix = 'quantity'): Record<string, number | null> {
  return COMMERCIAL_UNITS.reduce<Record<string, number | null>>((fields, unit) => {
    fields[quantitySetField(prefix, unit)] = set[unit] === null
      ? null
      : roundCommercialQuantity(Number(set[unit]));
    return fields;
  }, {});
}

/**
 * One shared presentation for an atomic quantity set. A screen may supply the
 * historical scalar as a fallback, but it must never attach a made-up unit to
 * that scalar. Every available dimensional view is rendered side by side.
 */
export function formatQuantitySet(
  source: Record<string, unknown> | null | undefined,
  prefix = 'quantity',
  language = 'pl',
  options: FormatQuantitySetOptions = {},
): string {
  const set = quantitySetFromFields(source, prefix);
  const views = COMMERCIAL_UNITS.flatMap((unit) => {
    const value = getQuantitySetValue(set, unit);
    if (value === null) return [];
    const displayValue = options.absolute ? Math.abs(value) : value;
    return [`${formatLocalizedQuantity(displayValue, language)} ${formatUnit(unit, language)}`];
  });
  if (views.length > 0) return views.join(' / ');

  const fallback = Number(options.fallbackQuantity);
  if (!Number.isFinite(fallback)) return options.emptyText ?? '—';
  const displayFallback = options.absolute ? Math.abs(fallback) : fallback;
  const label = options.fallbackUnit ? ` ${formatUnit(options.fallbackUnit, language)}` : '';
  return `${formatLocalizedQuantity(displayFallback, language)}${label}`;
}

export function resolveStoredQuantitySet(
  source: Record<string, unknown>,
  options: { prefix?: string; quantityField?: string; unitField?: string } = {},
): QuantitySet {
  const prefix = options.prefix || 'quantity';
  const quantityField = options.quantityField || 'quantity';
  const unitField = options.unitField || 'unit';
  const persisted = quantitySetFromFields(source, prefix);
  const rawUnit = source[unitField];
  // normalizeQuantityUnit deliberately defaults an empty value to pieces for
  // interactive calculators. Persisted data must be stricter: an unknown or
  // missing code cannot be silently relabelled as `pcs`.
  const normalizedUnit = typeof rawUnit === 'string' ? normalizeUnitCode(rawUnit) : null;
  const unit = isAllocationUnit(normalizedUnit) ? normalizedUnit : null;
  if (Object.values(persisted).some((value) => value !== null)) {
    return persisted;
  }
  return unit
    ? createQuantitySet(Number(source[quantityField]), unit, source as ConversionSource)
    : { pcs: null, m2: null, m3: null };
}

export function getQuantitySetValue(set: QuantitySet, unit: AllocationUnit): number | null {
  const value = set[unit];
  return value === null || !Number.isFinite(Number(value))
    ? null
    : roundCommercialQuantity(Number(value));
}

/** Resolve one persisted component without duplicating Pcs/M2/M3 field maps. */
export function getPersistedQuantityForUnit(
  source: Record<string, unknown> | null | undefined,
  unit: AllocationUnit | null | undefined,
  prefix = 'quantity',
): number | null {
  return unit ? getQuantitySetValue(quantitySetFromFields(source, prefix), unit) : null;
}

export function resolveSharedQuantityUnit(
  left: QuantitySet,
  right: QuantitySet,
  preferredUnit?: AllocationUnit | null,
): AllocationUnit | null {
  const candidates = preferredUnit
    ? [preferredUnit, ...COMMERCIAL_UNITS.filter((unit) => unit !== preferredUnit)]
    : [...COMMERCIAL_UNITS];
  return candidates.find((unit) => (
    getQuantitySetValue(left, unit) !== null
    && getQuantitySetValue(right, unit) !== null
  )) || null;
}

export function scaleQuantitySet(set: QuantitySet, factor: number): QuantitySet {
  if (!Number.isFinite(Number(factor))) return { pcs: null, m2: null, m3: null };
  if (Number(factor) === 0) return { pcs: 0, m2: 0, m3: 0 };
  return COMMERCIAL_UNITS.reduce<QuantitySet>((scaled, unit) => {
    const value = getQuantitySetValue(set, unit);
    scaled[unit] = value === null ? null : roundCommercialQuantity(value * factor);
    return scaled;
  }, { pcs: null, m2: null, m3: null });
}

/**
 * Rebase an immutable set to a new amount expressed in one of its available
 * units. Existing ratios are scaled; missing siblings stay missing.
 */
export function scaleQuantitySetToAmount(
  set: QuantitySet,
  amount: number,
  unit: AllocationUnit,
): QuantitySet | null {
  const roundedAmount = roundCommercialQuantity(Number(amount));
  const currentAmount = getQuantitySetValue(set, unit);
  if (!Number.isFinite(roundedAmount)) return null;
  if (currentAmount === null || Math.abs(currentAmount) < 1e-9) {
    return null;
  }

  const scaled = scaleQuantitySet(set, roundedAmount / currentAmount);
  scaled[unit] = roundedAmount;
  return scaled;
}

export function sumQuantitySets(sets: QuantitySet[]): QuantitySet {
  if (sets.length === 0) return { pcs: 0, m2: 0, m3: 0 };
  return COMMERCIAL_UNITS.reduce<QuantitySet>((sum, unit) => {
    const values = sets.map((set) => getQuantitySetValue(set, unit));
    sum[unit] = values.some((value) => value === null)
      ? null
      : roundCommercialQuantity(values.reduce<number>((total, value) => total + Number(value), 0));
    return sum;
  }, { pcs: null, m2: null, m3: null });
}

export function subtractQuantitySets(minuend: QuantitySet, subtrahend: QuantitySet): QuantitySet {
  return COMMERCIAL_UNITS.reduce<QuantitySet>((difference, unit) => {
    const left = getQuantitySetValue(minuend, unit);
    const right = getQuantitySetValue(subtrahend, unit);
    difference[unit] = left === null || right === null
      ? null
      : roundCommercialQuantity(left - right);
    return difference;
  }, { pcs: null, m2: null, m3: null });
}

export function quantitySetRatio(
  part: QuantitySet,
  whole: QuantitySet,
  preferredUnit?: AllocationUnit | null,
): { ratio: number | null; unit: AllocationUnit | null } {
  const unit = resolveSharedQuantityUnit(part, whole, preferredUnit);
  if (!unit) return { ratio: null, unit: null };

  const partValue = getQuantitySetValue(part, unit);
  const wholeValue = getQuantitySetValue(whole, unit);
  if (partValue === null || partValue < 0 || wholeValue === null || wholeValue <= 0) {
    return { ratio: null, unit };
  }

  return { ratio: partValue / wholeValue, unit };
}

export function isQuantitySetEmpty(set: QuantitySet): boolean {
  const values = COMMERCIAL_UNITS
    .map((unit) => getQuantitySetValue(set, unit))
    .filter((value): value is number => value !== null);
  return values.length === 0 || values.every((value) => Math.abs(value) < 0.001);
}

export function isQuantitySetBelowThreshold(
  quantity: QuantitySet,
  threshold: QuantitySet,
  preferredUnit?: AllocationUnit | null,
): { below: boolean; ratio: number | null; unit: AllocationUnit | null } {
  if (isQuantitySetEmpty(threshold)) return { below: false, ratio: null, unit: null };
  const { ratio, unit } = quantitySetRatio(quantity, threshold, preferredUnit);
  return { below: ratio !== null && ratio < 1 - 1e-9, ratio, unit };
}
